import { useCallback, useEffect, useRef } from 'react';
import { getNsmpIdFromSearch, syncNsmpIdInUrl } from '../utils/nsmpUrl';

/**
 * Mantém o modal NSMP sincronizado com `?nsmpId=` na URL (deep link e compartilhamento).
 */
export function useNsmpUrlSync({
  history,
  location,
  enabled = true,
  allRows,
  filteredRows,
  open,
  selectedId,
  setOpen,
  setSelectedId,
  setCurrentIndex,
  setReadOnly,
  cleanSelectedNsmp,
  getReadOnlyForDeepLink = () => true,
  onNsmpNotInFilter,
}) {
  const skipUrlParseRef = useRef(false);
  const filterResetForNsmpRef = useRef(null);

  const setUrlNsmpId = useCallback(
    (nsmpId) => {
      if (!enabled) return;
      skipUrlParseRef.current = true;
      syncNsmpIdInUrl(history, location, nsmpId, { replace: true });
    },
    [enabled, history, location],
  );

  const closeNsmpModal = useCallback(() => {
    setReadOnly(false);
    setOpen(false);
    setSelectedId(null);
    setCurrentIndex(null);
    setUrlNsmpId(null);
  }, [setReadOnly, setOpen, setSelectedId, setCurrentIndex, setUrlNsmpId]);

  const openNsmpModal = useCallback(
    ({ id, readOnly, index }) => {
      cleanSelectedNsmp();
      setSelectedId(id);
      setCurrentIndex(index);
      setReadOnly(readOnly);
      setOpen(true);
      setUrlNsmpId(id || null);
    },
    [cleanSelectedNsmp, setSelectedId, setCurrentIndex, setReadOnly, setOpen, setUrlNsmpId],
  );

  const syncCarouselNsmpId = useCallback(
    (id) => {
      if (id) setUrlNsmpId(id);
    },
    [setUrlNsmpId],
  );

  useEffect(() => {
    if (!enabled || !history || !location) return;

    if (skipUrlParseRef.current) {
      skipUrlParseRef.current = false;
      return;
    }

    const urlNsmpId = getNsmpIdFromSearch(location.search);

    if (!urlNsmpId) {
      filterResetForNsmpRef.current = null;
      if (open) {
        setReadOnly(false);
        setOpen(false);
        setSelectedId(null);
        setCurrentIndex(null);
      }
      return;
    }

    if (open && selectedId === urlNsmpId) return;

    const rows = allRows || [];
    if (!rows.length) return;

    const row = rows.find((r) => r.id === urlNsmpId);
    if (!row) {
      setUrlNsmpId(null);
      return;
    }

    let index = filteredRows.findIndex((r) => r.id === urlNsmpId);
    if (index < 0) {
      if (filterResetForNsmpRef.current !== urlNsmpId) {
        filterResetForNsmpRef.current = urlNsmpId;
        onNsmpNotInFilter?.();
      }
      return;
    }

    filterResetForNsmpRef.current = null;
    cleanSelectedNsmp();
    setSelectedId(urlNsmpId);
    setCurrentIndex(index);
    setReadOnly(getReadOnlyForDeepLink(row));
    setOpen(true);
  }, [
    enabled,
    history,
    location,
    location.search,
    allRows,
    filteredRows,
    open,
    selectedId,
    cleanSelectedNsmp,
    getReadOnlyForDeepLink,
    onNsmpNotInFilter,
    setUrlNsmpId,
    setOpen,
    setSelectedId,
    setCurrentIndex,
    setReadOnly,
  ]);

  return {
    openNsmpModal,
    closeNsmpModal,
    syncCarouselNsmpId,
  };
}
